import React, { useEffect, useRef, useState } from 'react';
import cx from 'classnames';
import { omit } from 'lodash';

import Collapse from '@magoosh/lib/collapse';
import Dropdown from '@magoosh/lib/dropdown';

import { GreenButton, WhiteButton } from '@marketing/components/buttons';
import { NavigationItemProps } from '../../types';

import MagooshLogoPurple from '@magoosh/assets/magoosh-logo-purple.png';
import MagooshLogoWhite from '@magoosh/assets/magoosh-logo-white.png';
import colors from '@magoosh/lib/styles/colors.module.scss';
import styles from './style.module.scss';

interface Props {
  darkMode?: boolean;
  isSticky: boolean;
  navigationItems: {
    items: NavigationItemProps[];
    supplementalItems: NavigationItemProps[];
  };
}

const NavBar: React.FC<Props> = (props) => {
  const ref = useRef<HTMLDivElement>(null);
  const [contentHeight, setContentHeight] = useState(0);
  const [isExpanded, setIsExpanded] = useState(false);

  const determineContentHeight = useRef(() => {
    if (ref.current) {
      setContentHeight(ref.current.clientHeight);
    }
  });

  // Items can pass a dispatchEvent prop that indicates special behavior.
  // At this time, the only special behavior is to launch the AI tutor.
  const items = props.navigationItems.items.map((navItem) => {
    if (navItem.dispatchEvent) {
      return {
        ...navItem,
        onClick: () => document.dispatchEvent(new Event(navItem.dispatchEvent))
      };
    } else {
      return navItem;
    }
  });

  const supplementalItems = props.navigationItems.supplementalItems.map((navItem) => {
    if (navItem.dispatchEvent) {
      return {
        ...navItem,
        onClick: () => document.dispatchEvent(new Event(navItem.dispatchEvent))
      };
    } else {
      return navItem;
    }
  });

  useEffect(() => {
    determineContentHeight.current();

    window.addEventListener('resize', determineContentHeight.current);

    return () => {
      window.removeEventListener('resize', determineContentHeight.current);
    };
  }, []);

  return (
    <nav
      className={cx({ 'expanded': isExpanded, [styles.darkMode]: !!props.darkMode })}
      style={{ position: 'relative' }}
    >
      <div
        className={cx(
          styles.navbar,
          'align-items-center d-flex flex-1 gap-s',
          { [styles.sticky]: props.isSticky }
        )}
      >
        <div className="flex-none">
          <a href="/">
            <img
              alt="Magoosh logo"
              className={cx(styles.logo, 'u-margin-R-xs', { [styles.sticky]: props.isSticky })}
              src={props.darkMode ? MagooshLogoWhite : MagooshLogoPurple}
            />
          </a>
        </div>

        <div className="d-flex flex-1 hidden-xs hidden-sm" style={{ height: '100%' }}>
          <ul className={styles.navbarItems}>
            {
              items.map((navItem, idx) => (
                <NavItem key={idx} {...navItem} darkMode={props.darkMode} />
              ))
            }
          </ul>
        </div>

        <div className="hidden-xs hidden-sm" style={{ height: '100%' }}>
          <ul className={styles.navbarItems}>
            {
              supplementalItems.map((navItem, idx) => (
                <NavItem key={idx} {...navItem} darkMode={props.darkMode} />
              ))
            }
          </ul>
        </div>

        <div className="flex-1 visible-xs visible-sm">
          <div className="d-flex flex-1 flex-lg-none justify-content-end">
            <button className={styles.navbarToggle} onClick={() => setIsExpanded(!isExpanded)}>
              <span className="sr-only">Toggle navigation</span>
              <span className={styles.iconBar} />
              <span className={styles.iconBar} />
              <span className={styles.iconBar} />
            </button>
          </div>
        </div>
      </div>

      <div
        className="visible-xs visible-sm"
        style={{
          background: props.darkMode ? '#222123' : '#fff',
          boxShadow: isExpanded ? '0 0 0 1px rgba(0, 0, 0, 0.1), 0 6px 6px -3px rgba(0, 0, 0, 0.07), 0 2px 2px -1px rgba(0, 0, 0, 0.07)' : 'none',
          height: isExpanded ? contentHeight : 0,
          marginLeft: -20,
          overflowY: 'auto',
          padding: '0 20px',
          position: 'absolute',
          top: props.isSticky ? 58 : 74,
          transition: 'height 200ms',
          width: 'calc(100% + 40px)'
        }}
      >
        <div className="d-flex flex-column u-padding-V-m" ref={ref} style={{ gap: 15 }}>
          <ul className={styles.navbarItems}>
            {
              items.map((navItem, idx) => (
                <MobileNavItem key={idx} {...navItem} darkMode={props.darkMode} />
              ))
            }
          </ul>

          <ul className={styles.navbarItems}>
            {
              supplementalItems.map((navItem, idx) => (
                <MobileNavItem key={idx} {...navItem} darkMode={props.darkMode} />
              ))
            }
          </ul>
        </div>
      </div>
    </nav>
  );
};

const NavItem = (props) => {
  if (props.button) {
    const Button = props.button === 'white' ? WhiteButton : GreenButton;

    return (
      <li className={styles.navItemButton}>
        <Button {...props} size="sm">
          <span dangerouslySetInnerHTML={{ __html: props.content }} />
        </Button>
      </li>
    );
  } else if (props.children) {
    return (
      <li style={{ position: 'relative' }}>
        <Dropdown>
          <Dropdown.Trigger style={{ height: '100%' }}>
            <a className="align-items-center d-flex gap-xs">
              <div
                dangerouslySetInnerHTML={{ __html: props.content }}
              />
              <Dropdown.Caret />
            </a>
          </Dropdown.Trigger>

          <Dropdown.Content>
            <ul className={styles.navbarChildItems}>
              {
                props.children.map((childNavItem, idx) => (
                  <li key={idx}>
                    <a
                      {...childNavItem}
                      dangerouslySetInnerHTML={{ __html: childNavItem.content }}
                    />
                  </li>
                ))
              }
            </ul>
          </Dropdown.Content>
        </Dropdown>
      </li>
    );
  } else {
    return (
      <li>
        <a
          {...omit(props, 'darkMode')}
          className="align-items-center d-flex gap-xxs"
        >
          <div dangerouslySetInnerHTML={{ __html: props.content }} />

          {
            props.badge && (
              <div
                style={{
                  backgroundColor: props.darkMode ? colors.purple : colors.purpleLight,
                  borderRadius: 20,
                  color:  props.darkMode ? colors.white : colors.purple,
                  fontSize: 10,
                  padding: '1px 6px'
                }}
              >
                <strong>{props.badge}</strong>
              </div>
            )
          }
        </a>
      </li>
    );
  }
};

const MobileNavItem = (props) => {
  if (props.button) {
    const Button = props.button === 'white' ? WhiteButton : GreenButton;

    return (
      <li className={styles.navItemButton}>
        <Button {...props} block size="sm">
          <span dangerouslySetInnerHTML={{ __html: props.content }} />
        </Button>
      </li>
    );
  } else if (props.children) {
    return (
      <li>
        <Collapse>
          <Collapse.Trigger>
            <a className="d-flex gap-xs">
              <div dangerouslySetInnerHTML={{ __html: props.content }} />
              <Collapse.Caret />
            </a>
          </Collapse.Trigger>

          <Collapse.Content>
            <ul className={styles.navbarChildItems}>
              {
                props.children.map((childNavItem, idx) => (
                  <li key={idx}>
                    <a
                      {...childNavItem}
                      dangerouslySetInnerHTML={{ __html: childNavItem.content }}
                    />
                  </li>
                ))
              }
            </ul>
          </Collapse.Content>
        </Collapse>
      </li>
    );
  } else {
    return (
      <li>
        <a
          {...omit(props, 'darkMode')}
          className="align-items-center d-flex gap-xxs"
        >
          <div dangerouslySetInnerHTML={{ __html: props.content }} />

          {
            props.badge && (
              <div
                style={{
                  backgroundColor: props.darkMode ? colors.purple : colors.purpleLight,
                  borderRadius: 20,
                  color:  props.darkMode ? colors.white : colors.purple,
                  fontSize: 10,
                  padding: '1px 6px'
                }}
              >
                <strong>{props.badge}</strong>
              </div>
            )
          }
        </a>
      </li>
    );
  }
};

export default NavBar;
